import { useCallback, useEffect, useState } from "react";

import type { BloodOxygenSaturationDataPoint } from "~/api/bloodOxygenSaturation";
import {
  getBloodOxygenSaturationData,
  getDateRange,
  getGroupByInterval,
} from "~/api/bloodOxygenSaturation";

interface UseBloodOxygenSaturationDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseBloodOxygenSaturationDataReturn {
  data: BloodOxygenSaturationDataPoint[];
  metadata: {
    avg: number;
    min: number;
    max: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useBloodOxygenSaturationData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseBloodOxygenSaturationDataProps): UseBloodOxygenSaturationDataReturn => {
  const [data, setData] = useState<BloodOxygenSaturationDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    avg: number;
    min: number;
    max: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(timeRange, navigationOffset, selectedDate);
      const groupByInterval = getGroupByInterval(timeRange);


      const response = await getBloodOxygenSaturationData({
        patient_id: patientId,
        group_by_interval: groupByInterval,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });

      setData(response.data);
      setMetadata(response.metadata);
    } catch (err) {
      console.error("Error fetching blood oxygen saturation data:", err);
      setError("Failed to fetch blood oxygen saturation data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
  }, [patientId, fetchData]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
